import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Eye, ShoppingCart, TrendingUp, Shield, Zap, BarChart3,
  ArrowRight, CheckCircle, ChevronRight, Users, Clock
} from 'lucide-react';

export default function BrandProofPage() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(60% 60% at 50% 30%, rgba(62,241,199,0.18) 0%, rgba(62,241,199,0) 60%)'
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-6">
              Commerce Index Proof
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Turn Real-Time Buyer Activity into
              <span className="text-[#3EF1C7]"> Trust, Urgency & Conversion</span>
            </h1>
            <p className="text-xl text-[#94A3B8] mb-8 leading-relaxed">
              Commerce Index Proof shows shoppers how many people are viewing and buying your products—in real time—and gives you the data to benchmark your product and category performance.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 text-lg px-8 py-6"
                data-testid="proof-cta-talk"
              >
                Talk to Us About Activating Proof
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Link to="/docs">
                <Button 
                  variant="outline" 
                  className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B] text-lg px-8 py-6"
                  data-testid="proof-cta-docs"
                >
                  View Developer Docs
                </Button>
              </Link>
            </div>
            
            {/* Already a customer strip */}
            <div className="mt-6 p-4 bg-[#1E293B]/50 rounded-lg border border-[#334155] inline-flex items-center gap-3">
              <span className="text-[#94A3B8] text-sm">Already using CI Proof?</span>
              <Link 
                to="/merchant/login" 
                className="text-[#3EF1C7] text-sm font-medium hover:underline flex items-center gap-1"
              >
                Go to the Merchant Portal <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Why Proof Matters Now
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Shoppers don&apos;t just want a price and a product description. They want reassurance, 
              evidence, and subtle signals of urgency.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Reassurance',
                desc: "They're not the only ones buying"
              },
              {
                icon: Shield,
                title: 'Evidence',
                desc: 'A product is active and trusted'
              },
              {
                icon: Clock,
                title: 'Urgency',
                desc: 'Others are looking at this right now'
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="bg-[#1E293B] border-[#334155] text-center">
                  <CardContent className="pt-8 pb-6">
                    <Icon className="w-12 h-12 text-[#3EF1C7] mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-[#94A3B8]">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="mt-12 p-6 bg-[#1E293B] rounded-xl border border-[#334155]">
            <p className="text-[#94A3B8] text-center">
              Right now, many brands fake &quot;live views&quot; counters, rely on generic countdown timers, or have no proof at all.
              <br />
              <span className="text-white font-medium">Shoppers are becoming more sophisticated. They can feel what&apos;s real and what&apos;s not.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
              The Solution
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Commerce Index Proof = Real Signals, Not Gimmicks
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Widget Preview */}
            <div className="relative">
              <div className="bg-[#1E293B] rounded-2xl p-8 border border-[#334155]">
                {/* Mock PDP Widget */}
                <div className="bg-[#0D1628] rounded-xl p-6 border border-[#22304A]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#3EF1C7]/20 flex items-center justify-center">
                      <Eye className="w-4 h-4 text-[#3EF1C7]" />
                    </div>
                    <span className="text-white font-medium">Live activity on this product</span>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-3 p-3 bg-[#1E293B] rounded-lg">
                      <span className="text-xl">👁️</span>
                      <span className="text-white"><strong>37</strong> people viewed this in the last hour</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-[#1E293B] rounded-lg">
                      <span className="text-xl">🛒</span>
                      <span className="text-white"><strong>12</strong> purchases in the last 24 hours</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 bg-[#3EF1C7]/10 rounded-lg border border-[#3EF1C7]/20">
                    <span className="text-xl">🔥</span>
                    <span className="text-[#3EF1C7] font-medium">This product is getting a lot of attention.</span>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#22304A] text-center">
                    <span className="text-xs text-[#64748B]">Powered by Commerce Index</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Benefits */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#3EF1C7]/10 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-6 h-6 text-[#3EF1C7]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Live Views</h3>
                  <p className="text-[#94A3B8]">
                    &quot;X people viewed this product in the last 60 minutes&quot;
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#7A5CFF]/10 flex items-center justify-center flex-shrink-0">
                  <ShoppingCart className="w-6 h-6 text-[#7A5CFF]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Recent Purchases</h3>
                  <p className="text-[#94A3B8]">
                    &quot;Y purchases in the last 24 hours&quot;
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
                <p className="text-[#94A3B8]">
                  Because Proof is powered by actual events (views and purchases), it:
                </p>
                <ul className="mt-3 space-y-2">
                  {[
                    'Builds trust instead of skepticism',
                    'Creates urgency without feeling manipulative',
                    'Gives you a data foundation you can re-use'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-white">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Integrate */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Why Integrate Proof?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                title: 'Lift Conversion on PDPs',
                desc: 'Social proof helps shoppers move from "researching" to "committing." When they see others viewing and buying, they\'re more confident in their decision.',
                color: '#3EF1C7'
              },
              {
                icon: Shield,
                title: 'Differentiate Your Brand',
                desc: 'Instead of generic timers and fake counters, you\'re showing real activity backed by a third-party index.',
                color: '#7A5CFF'
              },
              {
                icon: BarChart3,
                title: 'Unlock Richer Insights',
                desc: 'The same event stream powers product-level funnels, category-level CI insights, and cross-category benchmarking.',
                color: '#FFD166'
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="bg-[#1E293B] border-[#334155]">
                  <CardHeader>
                    <Icon className="w-10 h-10 mb-2" style={{ color: item.color }} />
                    <CardTitle className="text-white">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#94A3B8]">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
              Simple Integration
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              How It Works
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Get up and running in under 30 minutes
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: '1',
                title: 'Add a tiny script',
                desc: 'Your dev team adds a one-liner loader and 2-3 lines of tracking code.'
              },
              {
                step: '2',
                title: 'We start collecting',
                desc: 'Anonymous product views and purchases. No PII, no customer identity data.'
              },
              {
                step: '3',
                title: 'Proof goes live',
                desc: 'Our extension shows live view and purchase counts on your products.'
              },
              {
                step: '4',
                title: 'You get the data',
                desc: 'Dashboard access, exportable data, and future CI signals.'
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#3EF1C7] flex items-center justify-center text-[#0B1120] font-bold text-xl mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                </div>
                {i < 3 && (
                  <ChevronRight className="hidden lg:block absolute top-6 -right-3 w-6 h-6 text-[#334155]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Privacy */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-16 h-16 text-[#3EF1C7] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Real Proof. Real Privacy.
            </h2>
            <div className="space-y-4 text-left">
              {[
                'No emails, no names, no addresses collected',
                'We work with anonymous sessions and hashed identifiers',
                'You control which products and sites send data',
                'Proof is about honest signals, not surveillance'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-[#1E293B] rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0" />
                  <span className="text-[#E2E8F0]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E293B] to-[#0D1628] rounded-2xl p-12 border border-[#334155] text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to Replace Gimmicks with Real-Time Proof?
            </h2>
            <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              Build trust, nudge conversion, and feed a deeper Commerce Index for your category.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 text-lg px-8 py-6"
                data-testid="proof-cta-call"
              >
                Book a 20-min Call
              </Button>
              <Link to="/docs">
                <Button 
                  variant="outline" 
                  className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B] text-lg px-8 py-6"
                >
                  Request Integration Docs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
